import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, AlertTriangle, Lightbulb, Trophy, CheckCircle2, XCircle } from "lucide-react";
import { useIsMobile } from "./use-mobile";
import type { Topic, QuizQuestion } from "./data/mindMapData";

// Ataric logo as mascot placeholder (CRA gives URL string for img src)
import mascotImg from "../../assets/logo-4.svg";

interface MindMapProps {
  topics: Topic[];
  label: string;
  selectedTopic: Topic | null;
  onSelectTopic: (topic: Topic | null) => void;
}

const CANVAS_W = 1200;
const NODE_W = 320;
const NODE_H = 80;
const SPINE_X = CANVAS_W / 2;
const ROW_GAP = 150;
const TOP_PAD = 20;

/* ─── Vertical flowchart layout ─── */
function getNodePositions(topics: Topic[]) {
  const positions: { x: number; y: number; topic: Topic; side: "left" | "right"; order: number }[] = [];
  const startY = TOP_PAD + 280;

  topics.forEach((topic, i) => {
    const side: "left" | "right" = i % 2 === 0 ? "left" : "right";
    const x = side === "left" ? SPINE_X - 200 : SPINE_X + 200;
    const y = startY + i * ROW_GAP;
    positions.push({ x, y, topic, side, order: i + 1 });
  });

  return positions;
}

function getCanvasHeight(topicCount: number) {
  return TOP_PAD + 280 + topicCount * ROW_GAP + 160;
}

/* ─── Horizontal connection from spine to node ─── */
function ConnectionLine({ x1, y1, x2, y2, colorIndex, side, isMobile, order }: {
  x1: number; y1: number; x2: number; y2: number; colorIndex: number; side: "left" | "right"; isMobile: boolean; order: number;
}) {
  const d = `M ${x1},${y1} L ${x2},${y2}`;

  const hues = [270, 285, 255, 295, 245, 305, 275, 260, 290];
  const hue = hues[colorIndex % hues.length];
  const durNum = 1.2 + order * 0.08;
  const dur = `${durNum}s`;
  const beginNum = order * 0.3;
  const beginDelay = `${beginNum}s`;

  return (
    <g>
      <path d={d} fill="none" stroke={`hsl(${hue}, 80%, 65%)`}
        strokeWidth={isMobile ? 4 : 8} strokeOpacity={0.06} strokeLinecap="round" />
      <motion.path d={d} fill="none" stroke={`hsl(${hue}, 85%, 70%)`}
        strokeWidth={2} strokeOpacity={0.35} strokeLinecap="round"
        initial={isMobile ? {} : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={isMobile ? { duration: 0 } : { duration: 0.4, delay: order * 0.1 + 0.3, ease: "easeOut" }}
      />
      <circle r={isMobile ? 3 : 4} filter={isMobile ? undefined : "url(#orbGlow)"}>
        <animate attributeName="fill" values={`hsl(${hue}, 95%, 80%);hsl(${hue}, 100%, 90%);hsl(${hue}, 95%, 80%)`} dur="0.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0.9;0" dur={dur} begin={beginDelay} repeatCount="indefinite" />
        <animateMotion dur={dur} begin={beginDelay} repeatCount="indefinite" path={d} />
      </circle>
    </g>
  );
}
/* ─── Quiz Component ─── */
const QuizSection = ({ questions }: { questions: QuizQuestion[] }) => {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  const handleSelect = (qIndex: number, optIndex: number) => {
    if (revealed[qIndex]) return;
    setAnswers((a) => ({ ...a, [qIndex]: optIndex }));
    setRevealed((r) => ({ ...r, [qIndex]: true }));
  };

  const answeredCount = Object.keys(revealed).length;
  const correctCount = Object.entries(answers).filter(
    ([qi, oi]) => questions[Number(qi)].correctIndex === oi
  ).length;

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
      <h4 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary">
        <span className="h-px flex-1 bg-primary/20" />
        <Trophy className="h-3.5 w-3.5" /> Teste seus conhecimentos
        <span className="h-px flex-1 bg-primary/20" />
      </h4>

      <div className="space-y-4">
        {questions.map((q, qi) => {
          const isRevealed = revealed[qi];
          const selected = answers[qi];
          const isCorrect = selected === q.correctIndex;

          return (
            <motion.div
              key={qi}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55 + qi * 0.08 }}
              className="rounded-2xl border border-border/30 bg-card/50 p-4"
            >
              <p className="mb-3 text-sm font-bold text-foreground">
                {qi + 1}. {q.question}
              </p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  let optClass = "border-border/30 bg-card/30 hover:border-primary/30 hover:bg-card/60 cursor-pointer";
                  if (isRevealed) {
                    if (oi === q.correctIndex) {
                      optClass = "border-emerald-500/50 bg-emerald-500/10";
                    } else if (oi === selected && !isCorrect) {
                      optClass = "border-destructive/50 bg-destructive/10";
                    } else {
                      optClass = "border-border/20 bg-card/20 opacity-50";
                    }
                  }

                  return (
                    <button
                      key={oi}
                      onClick={() => handleSelect(qi, oi)}
                      disabled={isRevealed}
                      className={`flex w-full items-center gap-2 rounded-xl border p-3 text-left text-sm transition-all ${optClass}`}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/50 text-[11px] font-bold text-muted-foreground">
                        {String.fromCharCode(65 + oi)}
                      </span>
                      <span className="text-foreground">{opt}</span>
                      {isRevealed && oi === q.correctIndex && (
                        <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-emerald-500" />
                      )}
                      {isRevealed && oi === selected && !isCorrect && oi !== q.correctIndex && (
                        <XCircle className="ml-auto h-4 w-4 shrink-0 text-destructive" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {isRevealed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 overflow-hidden"
                  >
                    <div className={`rounded-xl p-3 text-sm ${isCorrect ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-destructive/10 border border-destructive/20"}`}>
                      <p className="font-bold mb-1">
                        {isCorrect ? "✅ Correto!" : "❌ Errado!"}
                      </p>
                      <p className="text-muted-foreground">{q.explanation}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Score summary */}
      <AnimatePresence>
        {answeredCount === questions.length && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-4 rounded-2xl border border-primary/30 bg-primary/10 p-4 text-center"
          >
            <p className="text-lg font-bold text-foreground">
              {correctCount === questions.length ? "🎉 Perfeito!" : correctCount >= 2 ? "👏 Muito bem!" : "💪 Continue praticando!"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Você acertou <span className="font-bold text-primary">{correctCount}</span> de <span className="font-bold">{questions.length}</span> questões
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── Detail Panel with Carousel ─── */
const DetailPanel = ({ topic, onClose }: { topic: Topic; onClose: () => void }) => {
  const [page, setPage] = useState(0);
  const totalPages = 3;
  const touchStartX = useRef(0);
  const touchDelta = useRef(0);

  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));
  const goPrev = () => setPage((p) => Math.max(0, p - 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDelta.current = 0;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchDelta.current = e.touches[0].clientX - touchStartX.current;
  };
  const handleTouchEnd = () => {
    if (touchDelta.current < -50) goNext();
    else if (touchDelta.current > 50) goPrev();
  };

  const pageLabels = ["📖 Conteúdo", "🎯 Subtópicos", "🏆 Quiz"];

  return (
    <motion.div
      initial={{ opacity: 0, x: 60 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 60 }}
      transition={{ duration: 0.35, type: "spring", damping: 22 }}
      data-detail-panel
      className="fixed inset-0 sm:left-auto sm:right-0 sm:w-[480px] z-[210] flex flex-col border-l border-primary/20 bg-background/95 backdrop-blur-2xl"
      style={{ boxShadow: "-20px 0 80px -15px hsl(270 91% 65% / 0.2)" }}
    >
      {/* Header */}
      <div className="shrink-0 p-4 sm:p-8 pb-3 sm:pb-4">
        <button onClick={onClose} className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 rounded-full bg-muted/50 p-2 sm:p-2.5 text-muted-foreground transition hover:bg-muted hover:text-foreground active:scale-95">
          <X className="h-5 w-5" />
        </button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="pr-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`flex h-12 w-12 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${topic.color} text-3xl sm:text-4xl border border-primary/20`}>
              {topic.icon}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold leading-tight text-foreground truncate">{topic.title}</h3>
              <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm leading-snug text-muted-foreground line-clamp-2">{topic.description}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Dots + Page Label */}
      <div className="shrink-0 flex flex-col items-center gap-1 sm:gap-1.5 pb-2 sm:pb-3">
        <div className="flex items-center gap-2">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === page ? "w-8 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}
        </div>
        <span className="text-[10px] sm:text-xs font-medium text-muted-foreground">{pageLabels[page]}</span>
      </div>

      {/* Carousel Content */}
      <div
        className="flex-1 min-h-0 overflow-hidden relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, x: 80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -80 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="absolute inset-0 overflow-y-auto scrollbar-hide px-4 sm:px-8 pb-4"
          >
            {page === 0 && (
              <div className="space-y-4 sm:space-y-6 pt-2">
                {topic.commonMistake && (
                  <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-3 sm:p-4">
                    <div className="mb-2 flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Erro comum do brasileiro
                    </div>
                    <div className="space-y-1 sm:space-y-1.5 text-xs sm:text-sm">
                      <p className="text-destructive/80">❌ {topic.commonMistake.wrong}</p>
                      <p className="font-medium text-foreground">✅ {topic.commonMistake.right}</p>
                      <p className="text-muted-foreground">{topic.commonMistake.explanation}</p>
                    </div>
                  </div>
                )}
                <div>
                  <h4 className="mb-2 sm:mb-3 flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-primary">
                    <span className="h-px flex-1 bg-primary/20" /> Exemplos práticos <span className="h-px flex-1 bg-primary/20" />
                  </h4>
                  <div className="space-y-2">
                    {topic.examples.map((ex, i) => (
                      <div key={i} className="rounded-xl border border-border/30 bg-card/50 p-3 sm:p-3.5 transition-all active:scale-[0.98]">
                        <p className="font-medium text-foreground text-sm">🇺🇸 {ex.english}</p>
                        <p className="mt-1 text-xs sm:text-sm text-muted-foreground">🇧🇷 {ex.portuguese}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {page === 1 && (
              <div className="space-y-4 sm:space-y-6 pt-2">
                <div>
                  <h4 className="mb-2 sm:mb-3 flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-primary">
                    <span className="h-px flex-1 bg-primary/20" /> O que você vai aprender <span className="h-px flex-1 bg-primary/20" />
                  </h4>
                  <div className="space-y-2">
                    {topic.subtopics.map((sub, i) => (
                      <div key={i} className="flex items-start gap-2.5 sm:gap-3 rounded-xl border border-border/30 bg-card/50 p-3 sm:p-3.5 transition-all active:scale-[0.98]">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground text-sm">{sub.title}</p>
                          <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground">{sub.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {topic.keywords && topic.keywords.length > 0 && (
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 sm:p-4">
                    <div className="mb-2 flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-primary">
                      <span className="h-px flex-1 bg-primary/20" /> Palavras-chave <span className="h-px flex-1 bg-primary/20" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {topic.keywords.map((kw, i) => (
                        <span key={i} className="inline-flex items-center rounded-lg border border-primary/25 bg-primary/10 px-3 py-1.5 text-xs sm:text-sm font-semibold text-primary">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {topic.tips && (
                  <div className="rounded-2xl border border-accent/20 bg-accent/5 p-3 sm:p-4">
                    <div className="mb-1 flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-accent">
                      <Lightbulb className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Dica
                    </div>
                    <p className="text-xs sm:text-sm leading-relaxed text-foreground">{topic.tips}</p>
                  </div>
                )}
              </div>
            )}

            {page === 2 && (
              <div className="pt-2">
                {topic.quiz && topic.quiz.length > 0 ? (
                  <QuizSection questions={topic.quiz} />
                ) : (
                  <p className="text-center text-muted-foreground text-sm py-8">Quiz em breve!</p>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation arrows */}
      <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-border/20" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <button
          onClick={goPrev}
          disabled={page === 0}
          className="flex items-center gap-1 sm:gap-1.5 rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all disabled:opacity-30 bg-muted/50 text-foreground hover:bg-muted active:scale-95"
        >
          <ChevronLeft className="h-4 w-4" /> Anterior
        </button>
        <span className="text-[10px] sm:text-xs text-muted-foreground">{page + 1} / {totalPages}</span>
        <button
          onClick={goNext}
          disabled={page === totalPages - 1}
          className="flex items-center gap-1 sm:gap-1.5 rounded-xl px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all disabled:opacity-30 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95"
        >
          Próximo <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

export const MindMap = ({ topics, label, selectedTopic, onSelectTopic }: MindMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const defaultZoom = isMobile ? 0.45 : 0.55;
  const [zoom, setZoom] = useState(defaultZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({ startX: 0, startY: 0, panX: 0, panY: 0 });

  const positions = useMemo(() => getNodePositions(topics), [topics]);
  const CANVAS_H = useMemo(() => getCanvasHeight(topics.length), [topics.length]);

  // Reset pan/zoom on topic change or device change
  useEffect(() => {
    setPan({ x: 0, y: 0 });
    setZoom(isMobile ? 0.45 : 0.55);
  }, [topics, isMobile]);

  // Non-passive wheel listener for zoom (fixes iOS/Chrome passive listener warning)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if ((e.target as HTMLElement).closest('[data-detail-panel]')) return;
      e.preventDefault();
      setZoom((z) => Math.min(2, Math.max(0.2, z + (e.deltaY > 0 ? -0.04 : 0.04))));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  const getTransform = useCallback(() => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (rect.width - CANVAS_W * zoom) / 2 + pan.x,
      y: pan.y,
    };
  }, [zoom, pan]);

  // Pinch-to-zoom for mobile
  const lastPinchDist = useRef(0);
  const handleTouchStartMap = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1) {
      if ((e.target as HTMLElement).closest("button")) return;
      setIsPanning(true);
      panRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, panX: pan.x, panY: pan.y };
    }
  }, [pan]);

  const handleTouchMoveMap = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      if (lastPinchDist.current > 0) {
        const scale = dist / lastPinchDist.current;
        setZoom((z) => Math.min(2, Math.max(0.2, z * scale)));
      }
      lastPinchDist.current = dist;
    } else if (isPanning && e.touches.length === 1) {
      setPan({
        x: panRef.current.panX + (e.touches[0].clientX - panRef.current.startX),
        y: panRef.current.panY + (e.touches[0].clientY - panRef.current.startY),
      });
    }
  }, [isPanning]);

  const handleTouchEndMap = useCallback(() => {
    setIsPanning(false);
    lastPinchDist.current = 0;
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if ((e.target as HTMLElement).closest("button")) return;
    setIsPanning(true);
    panRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  }, [pan]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if (!isPanning) return;
    setPan({
      x: panRef.current.panX + (e.clientX - panRef.current.startX),
      y: panRef.current.panY + (e.clientY - panRef.current.startY),
    });
  }, [isPanning]);

  const handlePointerUp = useCallback(() => setIsPanning(false), []);

  const offset = getTransform();

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden">

      {/* Zoom indicator */}
      <div className="absolute bottom-4 right-4 z-30 rounded-xl border border-border/40 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-lg backdrop-blur-md">
        {Math.round(zoom * 100)}%
      </div>

      {/* Draggable canvas */}
      <div
        className="h-full w-full cursor-grab active:cursor-grabbing select-none"
        onTouchStart={handleTouchStartMap}
        onTouchMove={handleTouchMoveMap}
        onTouchEnd={handleTouchEndMap}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{ touchAction: "none" }}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            width: CANVAS_W,
            height: CANVAS_H,
            position: "relative",
          }}
        >
          {/* SVG layer */}
          <svg width={CANVAS_W} height={CANVAS_H} className="absolute inset-0 pointer-events-none">
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="orbGlow">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Central vertical spine */}
            <motion.line
              x1={SPINE_X} y1={TOP_PAD + 120}
              x2={SPINE_X} y2={positions.length > 0 ? positions[positions.length - 1].y : TOP_PAD + 160}
              stroke="hsl(270 91% 65%)" strokeWidth={2} strokeOpacity={0.2} strokeLinecap="round"
              initial={isMobile ? {} : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={isMobile ? { duration: 0 } : { duration: 1.5, ease: "easeOut" }}
            />

            {/* Connection from spine to each node */}
            <g filter={isMobile ? undefined : "url(#glow)"}>
              {positions.map((pos, i) => {
                const nodeEdgeX = pos.side === "left" ? pos.x + NODE_W / 2 : pos.x - NODE_W / 2;
                return (
                  <ConnectionLine
                    key={pos.topic.id}
                    x1={SPINE_X}
                    y1={pos.y}
                    x2={nodeEdgeX}
                    y2={pos.y}
                    colorIndex={i}
                    side={pos.side}
                    isMobile={isMobile}
                    order={pos.order}
                  />
                );
              })}
            </g>

            {/* Pulse traveling down the spine */}
            {!isMobile && positions.length > 0 && (
              <circle r={4} filter="url(#orbGlow)">
                <animate attributeName="fill" values="hsl(270, 95%, 80%);hsl(270, 100%, 90%);hsl(270, 95%, 80%)" dur="0.6s" repeatCount="indefinite" />
                <animateMotion
                  dur={`${topics.length * 0.8}s`}
                  repeatCount="indefinite"
                  path={`M ${SPINE_X},${TOP_PAD + 120} L ${SPINE_X},${positions[positions.length - 1].y}`}
                />
              </circle>
            )}
          </svg>

          {/* Mascot + "Comece aqui" as the top of the spine */}
          <motion.div
            initial={isMobile ? { scale: 1 } : { scale: 0 }}
            animate={{ scale: 1 }}
            transition={isMobile ? { duration: 0 } : { duration: 0.6, type: "spring", damping: 12 }}
            className="absolute z-30 pointer-events-none"
            style={{ left: SPINE_X - 100, top: TOP_PAD - 10, width: 200 }}
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-1"
            >
              <img src={mascotImg} alt="Mascote" className="h-24 w-24 object-contain drop-shadow-[0_0_25px_hsl(270_91%_65%/0.7)]" />
              <span className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-base font-extrabold uppercase tracking-widest text-primary-foreground shadow-[0_0_40px_hsl(270_91%_65%/0.7)] glow-purple-strong whitespace-nowrap">
                Comece aqui
              </span>
              <span className="text-primary text-3xl leading-none">▼</span>
            </motion.div>
          </motion.div>

          {/* Topic nodes */}
          {positions.map((pos, i) => (
            <motion.div
              key={pos.topic.id}
              initial={isMobile ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={isMobile ? { duration: 0 } : { delay: pos.order * 0.1 + 0.2, duration: 0.5, type: "spring", damping: 16 }}
              className="absolute"
              style={{
                left: pos.x - NODE_W / 2,
                top: pos.y - NODE_H / 2,
                width: NODE_W,
                height: NODE_H,
              }}
            >

              {/* "Termine aqui" indicator on last node */}
              {pos.order === topics.length && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.6, duration: 0.6, type: "spring" }}
                  className="absolute -bottom-14 left-1/2 -translate-x-1/2 z-30 whitespace-nowrap"
                >
                  <motion.div
                    animate={{ y: [0, 5, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    className="flex flex-col items-center gap-0.5"
                  >
                    <span className="text-accent text-2xl leading-none">▲</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-5 py-2 text-sm font-extrabold uppercase tracking-widest text-accent-foreground shadow-[0_0_30px_hsl(290_80%_60%/0.6)]">
                      🏁 Termine aqui
                    </span>
                  </motion.div>
                </motion.div>
              )}

              <button
                onClick={() => onSelectTopic(selectedTopic?.id === pos.topic.id ? null : pos.topic)}
                className={`group relative flex h-full w-full items-center gap-4 rounded-2xl border-2 px-5 text-left transition-all duration-300 bg-gradient-to-br ${pos.topic.color} backdrop-blur-md ${
                  selectedTopic?.id === pos.topic.id
                    ? "border-primary/60 scale-110 shadow-[0_0_50px_-5px_hsl(270_91%_65%/0.6)]"
                    : "border-primary/15 hover:border-primary/40 hover:scale-105 hover:shadow-[0_0_35px_-8px_hsl(270_91%_65%/0.35)]"
                }`}
              >
                <div className="absolute inset-0 rounded-2xl bg-card/55" />
                <div className="absolute -inset-3 rounded-3xl bg-primary/8 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

                {/* Order badge */}
                <div className="absolute -top-3 -left-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-black shadow-lg glow-purple">
                  {pos.order}
                </div>

                <span className="relative z-10 shrink-0 text-3xl">{pos.topic.icon}</span>
                <span className="relative z-10 min-w-0 flex-1 text-[15px] font-extrabold leading-snug text-foreground">
                  {pos.topic.title}
                </span>
                <ChevronRight className="relative z-10 h-5 w-5 shrink-0 text-primary/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail panel overlay — no portal so clicks work; page z-index raised when open */}
      <AnimatePresence>
        {selectedTopic && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={`fixed inset-0 z-[205] bg-background/60 ${isMobile ? '' : 'backdrop-blur-md'}`}
              onClick={() => onSelectTopic(null)}
            />
            <DetailPanel topic={selectedTopic} onClose={() => onSelectTopic(null)} />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
